// ==========================================
// 2. CONTEXTO Y PROVIDER
// ==========================================

import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { isAbortError, toGraphQlError } from '../api/graphqlClient.ts'
import { MyGraphQlError } from '../services/GraphqlError.ts'

interface ErrorContextType {
  /** Muestra el error en un diálogo. Ignora las cancelaciones de peticiones (AbortError). */
  showError: (error: unknown) => void
}

/**
 * Convierte lo que llegue a showError en un Error para mostrar.
 * - Cancelación de petición: null (no se muestra, no es un fallo).
 * - MyGraphQlError: tal cual.
 * - Error de graphql-request sin normalizar (trae `response`): se convierte con toGraphQlError.
 * - Error, string u otro valor: se muestra su mensaje.
 */
const normalizarError = (err: unknown): Error | null => {
  if (isAbortError(err)) return null
  if (err instanceof MyGraphQlError) return err
  if (err instanceof Error && 'response' in err) return toGraphQlError(err)
  if (err instanceof Error) return err
  if (typeof err === 'string' && err) return new Error(err)
  return new Error('Ha ocurrido un error desconocido')
}

/**
 * Convierte en saltos de línea reales los "\\n" literales (texto escapado del backend) y las
 * etiquetas <br>, <br/> y <br />. El resultado se muestra como texto: ningún otro HTML se interpreta.
 */
const conSaltosDeLinea = (texto?: string | null) => texto?.replace(/\\n/g, '\n').replace(/<br\s*\/?>/gi, '\n')

const ErrorContext = createContext<ErrorContextType | undefined>(undefined)

export const useError = () => {
  const context = useContext(ErrorContext)
  if (!context) {
    throw new Error('useError debe ser usado dentro de un ErrorProvider')
  }
  return context
}

/**
 * Proveedor de datos para la generación de excepcion de graphql
 * @param children
 * @constructor
 */
export const ErrorProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  // Limpieza diferida del error al cerrar; se cancela si llega otro error antes
  const limpiarRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelarLimpieza = () => {
    if (limpiarRef.current) clearTimeout(limpiarRef.current)
    limpiarRef.current = null
  }

  useEffect(() => cancelarLimpieza, [])

  const showError = useCallback((err: unknown) => {
    const normalizado = normalizarError(err)
    if (!normalizado) return
    cancelarLimpieza()
    setError(normalizado)
    setOpen(true)
  }, [])

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setOpen(false)
    // Limpiar el error después de la animación de cierre
    cancelarLimpieza()
    limpiarRef.current = setTimeout(() => setError(null), 300)
  }

  // Extraemos propiedades de forma segura
  const esGraphQl = error instanceof MyGraphQlError
  const errorMessage = conSaltosDeLinea(error?.message) || 'Error inesperado'
  const errorName = conSaltosDeLinea(error?.name) || 'Error'
  // Mensaje original y stack: solo en desarrollo (MyGraphQlError ya los vacía en producción)
  const errorCause = import.meta.env.DEV && esGraphQl ? conSaltosDeLinea(error.cause) : undefined
  const errorStack = import.meta.env.DEV ? error?.stack : undefined

  // Acordeón: en producción solo muestra el código/ruta de los errores del servidor
  const hasTechnicalDetails = Boolean(errorCause || errorStack || esGraphQl)

  return (
    <ErrorContext.Provider value={{ showError }}>
      {children}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        closeAfterTransition={false}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: 'error.mainTextColor', fontWeight: 400, fontSize: '1.15rem' }}>
          ⚠️ Ha ocurrido un problema
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          {/* Mensaje principal */}
          <Alert
            severity="error"
            sx={{
              mb: 3,
              fontSize: '1.05rem',
              borderRadius: 2,
              // Respeta los saltos de línea del backend ("1. Error...\n2. Error...")
              '& .MuiAlert-message': { whiteSpace: 'pre-line', overflowWrap: 'anywhere' },
            }}
          >
            {errorMessage}
          </Alert>

          {/* Acordeón para detalles técnicos */}
          {hasTechnicalDetails && (
            <Accordion
              variant="outlined"
              sx={{
                boxShadow: 'none',
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                bgcolor: 'background.default',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Información avanzada del error
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 'bold', display: 'block' }}
                  >
                    CÓDIGO / RUTA:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-line' }}>
                    {errorName}
                  </Typography>
                </Box>

                {errorCause && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold', display: 'block' }}
                    >
                      MENSAJE ORIGINAL DEL SERVIDOR:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                      {errorCause}
                    </Typography>
                  </Box>
                )}

                {errorStack && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold', display: 'block' }}
                    >
                      STACKTRACE:
                    </Typography>
                    {/* El stack de MyGraphQlError es HTML armado por la app; el de otros errores se muestra como texto */}
                    <Box
                      component={esGraphQl ? 'div' : 'pre'}
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
                        color: (theme) => (theme.palette.mode === 'dark' ? 'grey.300' : 'grey.800'),
                        borderRadius: 2,
                        overflowX: 'auto',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        border: 1,
                        borderColor: 'divider',
                        whiteSpace: 'pre-wrap',
                        m: 0,
                        '& pre, & code': { margin: 0, fontFamily: 'inherit' },
                      }}
                      {...(esGraphQl
                        ? { dangerouslySetInnerHTML: { __html: errorStack } }
                        : { children: errorStack })}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="contained" color="primary" disableElevation autoFocus>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorContext.Provider>
  )
}
